"use client"

import { ConnectButton } from "@rainbow-me/rainbowkit"
import { useEffect } from "react"
import { useAccount, useBalance, useBlockNumber, useSwitchChain } from "wagmi"
import { Button, type ButtonProps } from "../../ui/Button"
import { useVaults } from "./useVault"
import { ConditionalWrapper } from "../../ui"
import { Tooltip } from "antd"

export const Web3Button = ({
  bypass = false,
  bypassSwitchNetwork = false,
  disconnectedText = "connect",
  delegateMessage,
  chainId,
  onClick,
  ...props
}: ButtonProps & {
  bypass?: boolean
  bypassSwitchNetwork?: boolean
  chainId?: number
  disconnectedText?: string
  delegateMessage?: string
}) => {
  const { chains, switchChain } = useSwitchChain()

  const { data: blockNumber } = useBlockNumber({ watch: true })
  const { address } = useAccount()
  const { effectiveAddress } = useVaults()
  const isUsingDelegate = effectiveAddress !== address
  // const chain = useChains()
  // const nativeCurrency = chain.find((c) => c.id === chainId)?.nativeCurrency
  const balance = useBalance({
    address,
  })

  useEffect(() => {
    balance.refetch()
  }, [blockNumber, balance])
  return (
    <ConnectButton.Custom>
      {({
        account,
        chain,
        openChainModal,
        openConnectModal,
        authenticationStatus,
        mounted,
      }) => {
        const ready = mounted && authenticationStatus !== "loading"
        const connected =
          ready &&
          account &&
          chain &&
          (!authenticationStatus || authenticationStatus === "authenticated")

        if (bypass) {
          return <Button {...props} />
        }

        if (!ready) {
          return (
            <div
              aria-hidden
              style={{
                opacity: 0,
                pointerEvents: "none",
                userSelect: "none",
              }}
            >
              <Button {...props}>{props.children}</Button>
            </div>
          )
        }

        if (!connected) {
          return (
            <Button
              htmlType="button"
              onClick={openConnectModal}
              className={props.className}
              {...props}
              disabled={false}
            >
              {disconnectedText}
            </Button>
          )
        }

        if (
          chain.unsupported ||
          (chainId && chain.id !== chainId && !bypassSwitchNetwork)
        ) {
          return (
            <Button
              {...props}
              htmlType="button"
              disabled={false}
              onClick={() => {
                const tgt = chains.find(({ id }) => id === chainId)
                if (!tgt) {
                  throw new Error("Chain not found")
                }
                if (chainId) {
                  switchChain({ chainId: chainId! })
                  return
                }
                openChainModal()
              }}
              className={props.className}
            >
              Change chain
            </Button>
          )
        }

        // TODO: We skip this as when using AA we'd neeed to read into AA.getDeposit()
        // and not the native balance.
        // if (balance.data?.value === BigInt(0)) {
        //   return (
        //     <Button
        //       {...props}
        //       htmlType="button"
        //       onClick={(evt) => {
        //         evt.preventDefault()
        //         messageApi.open({
        //           type: "error",
        //           content: (
        //             <span className="text-black">
        //               You don't have enough ETH in your wallet to perform this
        //               transaction.
        //             </span>
        //           ),
        //         })
        //       }}
        //     >
        //       {contextHolder}
        //       {props.children}
        //     </Button>
        //   )
        // }
        // some actions, such as sending $LOCKS, cannot be performed by
        // a delegate so we need to gate it
        const preventBecauseDelegate =
          Boolean(isUsingDelegate) && Boolean(delegateMessage)
        return (
          <ConditionalWrapper
            if={preventBecauseDelegate}
            with={Tooltip}
            props={{ title: delegateMessage }}
          >
            <Button
              onClick={onClick}
              {...props}
              disabled={props.disabled || preventBecauseDelegate}
            >
              {props.children || (
                <>
                  {account.displayName}
                  {account.displayBalance ? ` (${account.displayBalance})` : ""}
                </>
              )}
            </Button>
          </ConditionalWrapper>
        )
      }}
    </ConnectButton.Custom>
  )
}
